/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import { useVideoContext } from '@brands/hooks';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import cn from 'classnames';
import moment from 'moment';
import React, { FC, memo, useEffect, useState } from 'react';
import { RemoteParticipant } from 'twilio-video';

import { useAppSelector } from '../../hooks/useReduxHook';
import ProviderNotJoinedModal from '../ProviderNotJoinedModal/ProviderNotJoinedModal';
import SelectBackgroundModal from '../SelectBackgroundModal/SelectBackgroundModal';
import VideoApp from '../VideoApp';
import styles from './styles.module.scss';

type VideoRoomProps = {
  endCurrentCall?: boolean;
  uploadFile?: (file: File) => Promise<void>;
  setHasJoinedFlag?: React.Dispatch<React.SetStateAction<boolean>>;
  alreadyJoined?: boolean | undefined;
  appointmentStartTime?: Date | null;
  isPatientInRoom?: boolean;
  hasPatientAttended?: boolean;
  layout?: 'mini' | 'full';
};

const handleKeyDown = (event: any): void => {
  if (event.key === 'Tab' || event.key === ' ') {
    event.preventDefault();
  }
};

const VideoRoom: FC<VideoRoomProps> = ({
  endCurrentCall,
  uploadFile,
  setHasJoinedFlag,
  alreadyJoined,
  appointmentStartTime,
  isPatientInRoom,
  hasPatientAttended,
  layout,
}) => {
  const { is_visible: isVisible, mobileModalMode } = useAppSelector(selectVideoCallCredentials);
  const { userInfo } = useAppSelector(selectAuth);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!isVisible && userInfo.role.name === UserRoleName.Patient) {
        event.preventDefault();
        event.returnValue = '';
        window.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isVisible]);

  const isMini = layout === 'mini';
  const isModalFull = layout === 'full';

  return (
    <div
      className={cn(
        styles.videoRoom,
        isMini ? styles.videoRoomMini : styles.videoRoomFull,
        isModalFull ? styles.videoRoomModalFull : '',
        mobileModalMode !== 'fullscreen' && styles.maxHeightAdjust,
        userInfo?.role?.name === UserRoleName.Patient ? styles.videoRoomFullPatient : '',
      )}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.videoBody} style={{ paddingTop: isVisible ? '0px' : '', height: isVisible ? '100%' : '' }}>
        <div className={styles.videoContainer}>
          <VideoApp
            layout={layout}
            endCurrentCall={endCurrentCall}
            uploadFile={uploadFile}
            setHasJoinedFlag={setHasJoinedFlag}
            alreadyJoined={alreadyJoined}
            appointmentStartTime={
              // The downstream end-call warning covers the no-show payment
              // rule only, so it must never fire once the patient has been
              // on the call, including after they hang up first.
              userInfo.role.name === UserRoleName.Provider && !isPatientInRoom && !hasPatientAttended
                ? appointmentStartTime
                : null
            }
          />
        </div>
      </div>
      <SelectBackgroundModal />
      {userInfo?.role?.name === UserRoleName.Patient && <ProviderNotJoinedModal />}
    </div>
  );
};

VideoRoom.defaultProps = {
  endCurrentCall: false,
  uploadFile: undefined,
  setHasJoinedFlag: undefined,
  alreadyJoined: false,
  isPatientInRoom: false,
  hasPatientAttended: false,
};

export default memo(VideoRoom);
